'use client';

/**
 * Settings, the integrations tab: withdraw the browser assistant's access
 * (ADR-0049 "Open before production"; owner decision 2026-10-01). "You can
 * turn this off in Settings" is the dialog's promise, and this row is where
 * it is kept.
 *
 * Self-contained: it reads the record through GET /api/workspaces/webmcp-
 * consent (the session is the only identity) and renders nothing until that
 * says allowed, so a person who was never asked, or who already withdrew,
 * sees no row at all. Nothing is rendered either when the rail is off for
 * this environment (`webmcpTools` in RuntimeConfig), read in the effect
 * rather than in render so the server and the first client paint agree.
 *
 * REMOVE HAS TO REACH THE PAGES THAT ARE OPEN (S-382, 2026-10-01). It posts
 * `{ allow: false }` and, once the server has confirmed it, announces the
 * removal to this browser's other pages (`openWebMcpConsentChannel`), whose
 * workspace bridges drop their copy of the record at once and ask again on
 * the next call. A page on another device has no such channel and asks again
 * when its copy expires (lib/workspaces/webmcp-consent-gate.ts). The first
 * build did neither: every open page kept honouring the Allow it had read at
 * load. The announcement comes AFTER the server's answer on purpose, so a
 * page that re-reads because of it reads the removal.
 *
 * Drawn in the row anatomy of the neighbouring sections in
 * components/connected-accounts-tab.tsx (a bordered section, a title, a
 * bordered row with the action on the right as the same ghost trash button);
 * mounted by components/settings/settings-view.tsx right after that tab.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Trash2 } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { WEBMCP_CONSENT_ROUTE } from '@/lib/workspaces/webmcp-consent';
import { openWebMcpConsentChannel, type WebMcpConsentChannel } from '@/lib/workspaces/webmcp-consent-gate';

export function BrowserAssistantAccess() {
  const t = useT();
  const [allowedAt, setAllowedAt] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState(false);
  const channel = useRef<WebMcpConsentChannel | null>(null);

  useEffect(() => {
    if (!getRuntimeConfig().webmcpTools) return;
    let cancelled = false;
    channel.current = openWebMcpConsentChannel();
    void (async () => {
      try {
        const r = await fetch(WEBMCP_CONSENT_ROUTE, { credentials: 'same-origin' });
        if (!r.ok || cancelled) return;
        const body = (await r.json()) as { allowed?: unknown; at?: unknown };
        if (!cancelled && body.allowed === true && typeof body.at === 'string') setAllowedAt(body.at);
      } catch {
        /* best-effort: no row */
      }
    })();
    return () => {
      cancelled = true;
      channel.current?.close();
      channel.current = null;
    };
  }, []);

  const remove = useCallback(async () => {
    setRemoving(true);
    setFailed(false);
    try {
      const r = await fetch(WEBMCP_CONSENT_ROUTE, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ allow: false }),
      });
      if (r.ok) {
        setAllowedAt(null);
        channel.current?.announceRemoved();
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    } finally {
      setRemoving(false);
    }
  }, []);

  if (!allowedAt) return null;

  const when = new Date(allowedAt);
  const date = Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString();

  return (
    <section className="mt-5 rounded-lg border border-border p-4">
      <h3 className="mb-2 text-sm font-semibold">{t('settings.browserAssistantTitle')}</h3>
      <p className="mb-3 text-xs text-muted-foreground">{t('settings.browserAssistantDescription')}</p>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <Sparkles className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 truncate text-sm font-medium">{t('settings.browserAssistantAllowedOn', { date })}</div>
        </div>
        <button
          type="button"
          className="btn-ghost p-1.5 text-destructive"
          disabled={removing}
          onClick={remove}
          aria-label={t('settings.browserAssistantRemove')}
        >
          {removing ? <Spinner size="sm" /> : <Trash2 className="h-3.5 w-3.5" />}
        </button>
      </div>
      {failed && <p className="mt-2 text-xs text-destructive">{t('settings.browserAssistantRemoveFailed')}</p>}
    </section>
  );
}
